import React, { useEffect, useRef, useState } from 'react';
import {
  FormLabel,
  Input,
  FormControl,
  FormErrorMessage,
  Button,
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  useColorModeValue,
  Grid,
  GridItem,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Icon,
  Flex,
  Select
} from '@chakra-ui/react';
import { Formik, Form, Field, FieldProps } from 'formik';
import * as Yup from 'yup';
import { NumericFormat } from 'react-number-format';
import { FaCreditCard, FaLock, FaCalendarAlt, FaUser, FaIdCard } from 'react-icons/fa';
import { makePostPaywayPaymentCall } from '../services/payment-service';
import { PostPaymentRequest } from '../models/web/post-payment-request';
import { useToastHelpers } from '../utils/toast-helpers';
import { useNavigate } from 'react-router-dom';
import { MY_MEMBERSHIP_FEES_PAGE_ROUTE, PAYMENT_METHOD_ID_PAYWAY, PAYWAY_API_URL, PAYWAY_PUBLIC_KEY } from '../constants';

declare global {
  interface Window {
    Decidir: any;
    decidir: any;
  }
}

interface CreditCardPreviewProps {
  cardNumber: string;
  cardHolder: string;
  expirationMonth: string;
  expirationYear: string;
  securityCode: string;
  isFlipped: boolean;
  cardType: string;
  cardBrand: string;
}

const CreditCardPreview: React.FC<CreditCardPreviewProps> = ({
  cardNumber,
  cardHolder,
  expirationMonth,
  expirationYear,
  securityCode,
  isFlipped,
  cardType,
  cardBrand
}) => {
  const cardBg = useColorModeValue(
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #2d3748 0%, #4a5568 100%)'
  );
  const textColor = 'white';

  const formatCardNumber = (number: string) => {
    const cleaned = number.replace(/\s/g, '');
    const masked = cleaned.replace(/./g, '•').slice(0, -4) + cleaned.slice(-4);
    return masked.replace(/(.{4})/g, '$1 ').trim() || '•••• •••• •••• ••••';
  };

  const getCardDisplayText = () => {
    if (!cardBrand && !cardType) {
      return 'VISA/MASTERCARD/CABAL';
    }

    const brandMap: { [key: string]: string } = {
      'VISA': 'VISA',
      'MASTERCARD': 'MASTERCARD',
      'CABAL': 'CABAL'
    };

    const typeMap: { [key: string]: string } = {
      'DEBIT': 'DÉBITO',
      'CREDIT': 'CRÉDITO',
      'PREPAID': 'PREPAGA'
    };

    const brand = brandMap[cardBrand] || '';
    const type = typeMap[cardType] || '';

    if (brand && type) {
      return `${brand} ${type}`;
    } else if (brand) {
      return brand;
    } else if (type) {
      return type;
    }

    return 'VISA/MASTERCARD/CABAL';
  };

  return (
    <Box
      w="320px"
      h="200px"
      position="relative"
      style={{
        transformStyle: "preserve-3d"
      }}
      transition="transform 0.6s"
      transform={isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'}
    >
      {/* Front of card */}
      <Box
        position="absolute"
        w="full"
        h="full"
        background={cardBg}
        borderRadius="xl"
        p={6}
        boxShadow="xl"
        style={{
          backfaceVisibility: "hidden"
        }}
        color={textColor}
      >
        <VStack spacing={4} align="stretch" h="full">
          <HStack justify="space-between">
            <Icon as={FaCreditCard} size="24px" />
            <Text fontSize="sm" fontWeight="semibold">{getCardDisplayText()}</Text>
          </HStack>

          <Box flex="1" display="flex" alignItems="center">
            <Text fontSize="lg" fontFamily="monospace" letterSpacing="2px">
              {formatCardNumber(cardNumber)}
            </Text>
          </Box>

          <HStack justify="space-between" align="end">
            <VStack align="start" spacing={1}>
              <Text fontSize="xs" opacity={0.8}>NOMBRE DEL TITULAR</Text>
              <Text fontSize="sm" fontWeight="semibold" textTransform="uppercase" fontFamily="monospace" letterSpacing="1px">
                {cardHolder || 'NOMBRE APELLIDO'}
              </Text>
            </VStack>
            <VStack align="end" spacing={1}>
              <Text fontSize="xs" opacity={0.8}>VENCE</Text>
              <Text fontSize="sm" fontWeight="semibold" fontFamily="monospace" letterSpacing="1px">
                {expirationMonth || 'MM'}/{expirationYear || 'AA'}
              </Text>
            </VStack>
          </HStack>
        </VStack>
      </Box>

      {/* Back of card */}
      <Box
        position="absolute"
        w="full"
        h="full"
        background={cardBg}
        borderRadius="xl"
        transform="rotateY(180deg)"
        style={{
          backfaceVisibility: "hidden"
        }}
        boxShadow="xl"
      >
        <VStack spacing={0} h="full">
          <Box w="full" h="50px" bg="black" mt={4} />
          <Box w="full" p={6} flex="1">
            <HStack justify="space-between" align="center" h="full">
              <Box>
                <Text color="black" fontSize="xs">CVV</Text>
                <Box bg="white" px={3} py={1} borderRadius="md" w="60px">
                  <Text color="black" fontSize="sm" fontFamily="monospace" letterSpacing="1px">
                    {securityCode || '•••'}
                  </Text>
                </Box>
              </Box>
              <VStack align="end" spacing={2}>
                <Icon as={FaCreditCard} color="white" size="20px" />
                <Text color="white" fontSize="xs">SEGURO</Text>
              </VStack>
            </HStack>
          </Box>
        </VStack>
      </Box>
    </Box>
  );
};

const CardNumberInput = ({ field, form }: FieldProps) => {
  const borderColor = useColorModeValue('gray.300', 'gray.600');

  return (
    <NumericFormat
      {...field}
      customInput={Input}
      format="#### #### #### ####"
      mask="_"
      placeholder="1234 5678 9012 3456"
      data-decidir="card_number"
      borderColor={borderColor}
      size="lg"
      fontFamily="monospace"
      letterSpacing="1px"
      onChange={(e) => {
        form.setFieldValue(field.name, e.target.value.replace(/\s/g, ''));
      }}
    />
  );
};

const CvvInput = ({ field, onFocus, onBlur }: FieldProps & { onFocus?: () => void; onBlur?: () => void }) => {
  const borderColor = useColorModeValue('gray.300', 'gray.600');

  return (
    <NumericFormat
      {...field}
      customInput={Input}
      format="###"
      mask="_"
      placeholder="123"
      data-decidir="security_code"
      borderColor={borderColor}
      size="lg"
      fontFamily="monospace"
      letterSpacing="1px"
      onFocus={onFocus}
      onBlur={onBlur}
    />
  );
};

const ExpirationDateInput = ({ field, form }: FieldProps) => {
  const borderColor = useColorModeValue('gray.300', 'gray.600');
  const currentYear = new Date().getFullYear();
  const currentYearShort = currentYear % 100;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, '');

    if (value.length > 4) {
      value = value.substring(0, 4);
    }

    let formattedValue = value;
    if (value.length >= 2) {
      formattedValue = value.substring(0, 2) + '/' + value.substring(2);
    }

    let isValid = true;
    if (value.length >= 2) {
      const month = parseInt(value.substring(0, 2));
      if (month < 1 || month > 12) {
        isValid = false;
      }
    }

    if (value.length === 4) {
      const year = parseInt(value.substring(2, 4));
      if (year < currentYearShort || year > 99) {
        isValid = false;
      }
    }

    if (isValid || value.length < field.value.replace('/', '').length) {
      form.setFieldValue(field.name, formattedValue);

      if (value.length >= 2) {
        const month = value.substring(0, 2);
        form.setFieldValue('expirationMonth', month);
      } else {
        form.setFieldValue('expirationMonth', '');
      }

      if (value.length === 4) {
        const year = value.substring(2, 4);
        form.setFieldValue('expirationYear', year);
      } else {
        form.setFieldValue('expirationYear', '');
      }
    }
  };

  return (
    <>
      <Input
        name={field.name}
        value={field.value}
        onChange={handleChange}
        placeholder="MM/AA"
        borderColor={borderColor}
        size="lg"
        maxLength={5}
        onBlur={field.onBlur}
        fontFamily="monospace"
        letterSpacing="1px"
      />
    </>
  );
};

const DocumentNumberInput = ({ field }: FieldProps) => {
  const borderColor = useColorModeValue('gray.300', 'gray.600');

  return (
    <NumericFormat
      {...field}
      customInput={Input}
      format="########"
      mask="_"
      placeholder="12345678"
      data-decidir="card_holder_doc_number"
      borderColor={borderColor}
      size="lg"
      fontFamily="monospace"
      letterSpacing="1px"
    />
  );
};

const CardHolderInput = ({ field }: FieldProps) => {
  const borderColor = useColorModeValue('gray.300', 'gray.600');

  return (
    <Input
      {...field}
      data-decidir="card_holder_name"
      placeholder="Nombre del titular"
      borderColor={borderColor}
      size="lg"
      fontFamily="monospace"
      letterSpacing="1px"
      textTransform="uppercase"
    />
  );
};

const validationSchema = Yup.object().shape({
  cardType: Yup.string()
    .required('El tipo de tarjeta es requerido'),
  cardBrand: Yup.string()
    .required('La marca de tarjeta es requerida'),
  cardNumber: Yup.string()
    .required('El número de tarjeta es requerido')
    .matches(/^\d{16}$/, 'El número de tarjeta debe tener 16 dígitos'),
  securityCode: Yup.string()
    .required('El CVV es requerido')
    .matches(/^\d{3}$/, 'El CVV debe tener 3 dígitos'),
  expirationDate: Yup.string()
    .required('La fecha de expiración es requerida')
    .matches(/^\d{2}\/\d{2}$/, 'Ingrese una fecha válida (MM/AA)')
    .test('valid-date', 'Ingrese una fecha válida', function(value) {
      if (!value || !value.includes('/')) return false;

      const [monthStr, yearStr] = value.split('/');
      const month = parseInt(monthStr);
      const year = parseInt(yearStr);
      const currentYear = new Date().getFullYear() % 100;

      if (month < 1 || month > 12) return false;
      return !(year < currentYear || year > 99);
    }),
  expirationMonth: Yup.string(),
  expirationYear: Yup.string(),
  cardHolder: Yup.string()
    .required('El nombre del titular es requerido')
    .min(3, 'El nombre debe tener al menos 3 caracteres'),
  cardHolderId: Yup.string()
    .required('El DNI es requerido')
    .matches(/^\d{7,8}$/, 'El DNI debe tener 7 u 8 dígitos')
});

let isScriptLoading = false;
let isScriptLoaded = false;

// Immediate global error suppression - must be applied BEFORE any Payway script loads
if (typeof window !== 'undefined') {
  // Store original console methods immediately
  const _originalConsoleError = console.error;
  const _originalConsoleWarn = console.warn;

  // Aggressively override console.error globally
  console.error = (...args: any[]) => {
    const fullMessage = args.join(' ');
    const firstArg = String(args[0] || '');

    // Comprehensive Payway error detection
    if (
      fullMessage.includes('decidir') ||
      fullMessage.includes('frauddetectionconf') ||
      fullMessage.includes('requests.js') ||
      fullMessage.includes('traffic.js') ||
      fullMessage.includes('404 (Not Found)') ||
      firstArg.includes('GET https://developers.decidir.com') ||
      firstArg.includes('developers.decidir.com') ||
      args.some(arg => String(arg).includes('decidir.com'))
    ) {
      return; // Completely suppress these errors
    }
    _originalConsoleError.apply(console, args);
  };

  console.warn = (...args: any[]) => {
    const fullMessage = args.join(' ');
    if (
      fullMessage.includes('decidir') ||
      fullMessage.includes('frauddetectionconf') ||
      fullMessage.includes('developers.decidir.com')
    ) {
      return;
    }
    _originalConsoleWarn.apply(console, args);
  };

  // Override XMLHttpRequest immediately at prototype level
  const _originalXHROpen = XMLHttpRequest.prototype.open;
  const _originalXHRSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function(method: string, url: string | URL, async?: boolean, user?: string | null, password?: string | null) {
    (this as any)._url = String(url);
    (this as any)._isPaywayRequest = String(url).includes('decidir.com');
    return _originalXHROpen.call(this, method, url, async ?? true, user, password);
  };

  XMLHttpRequest.prototype.send = function(body?: Document | XMLHttpRequestBodyInit | null) {
    if ((this as any)._isPaywayRequest) {
      // Completely override error handling for Payway requests
      this.addEventListener('error', (e) => {
        e.stopImmediatePropagation();
        e.preventDefault();
        return false;
      }, true);

      this.addEventListener('loadend', (e) => {
        if (this.status === 404 && (this as any)._url?.includes('frauddetectionconf')) {
          e.stopImmediatePropagation();
          e.preventDefault();
        }
      }, true);
    }
    return _originalXHRSend.call(this, body);
  };

  // Override window.onerror immediately
  window.onerror = (message, source) => {
    const messageStr = String(message || '');
    const sourceStr = String(source || '');
    return sourceStr.includes('decidir.js') ||
      sourceStr.includes('requests.js') ||
      sourceStr.includes('traffic.js') ||
      messageStr.includes('decidir') ||
      messageStr.includes('frauddetectionconf') ||
      messageStr.includes('i is not a function');
  };
}

/*
 * IMPORTANT NOTE ABOUT PAYWAY ERROR SUPPRESSION:
 *
 * The Payway (Decidir) SDK generates internal errors that are outside our control:
 * - GET https://developers.decidir.com/api/v2/frauddetectionconf 404 (Not Found)
 * - TypeError: i is not a function (decidir.js)
 *
 * These errors don't affect payment functionality but clutter the console.
 * The interceptors below specifically suppress these Payway errors
 * while keeping other important application errors visible.
 */

interface Props {
  payment: PostPaymentRequest;
}

const PaywayCheckout = (props: Props) => {
  const formRef: React.RefObject<HTMLFormElement> = useRef(null);
  const [isDecidirLoaded, setIsDecidirLoaded] = useState(false);
  const [isCardFlipped, setIsCardFlipped] = useState(false);
  const navigate = useNavigate();
  const { showSuccessToast, showErrorToast } = useToastHelpers();
  const [formData] = useState({
    cardType: '',
    cardBrand: '',
    cardNumber: '',
    securityCode: '',
    expirationDate: '',
    expirationMonth: '', // Hidden field for Payway
    expirationYear: '',  // Hidden field for Payway
    cardHolder: '',
    cardHolderId: ''
  });

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const headingColor = useColorModeValue('gray.800', 'gray.100');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const headerBg = useColorModeValue('green.50', 'green.900');
  const securityBadgeBg = useColorModeValue('green.50', 'green.900');
  const securityBadgeBorder = useColorModeValue('green.200', 'green.700');

  useEffect(() => {

    const loadDecidir = async () => {
      if (isScriptLoaded && window.decidir) {
        setIsDecidirLoaded(true);
        return;
      }

      if (isScriptLoading) {
        return;
      }

      try {
        const scriptExists = document.querySelector('script[src*="decidir.js"]');

        if (!scriptExists) {
          isScriptLoading = true;
          const script = document.createElement("script");
          script.src = "https://live.decidir.com/static/v2.6.4/decidir.js";
          script.async = true;

          script.onload = () => {
            isScriptLoading = false;
            isScriptLoaded = true;
            setTimeout(() => {
              initializeDecidir();
            }, 100);
          };

          script.onerror = () => {
            isScriptLoading = false;
            setIsDecidirLoaded(false);
          };

          document.body.appendChild(script);
        } else {
          isScriptLoaded = true;
          setTimeout(() => {
            initializeDecidir();
          }, 100);
        }
      } catch (error) {
        isScriptLoading = false;
        setIsDecidirLoaded(false);
      }
    };

    const initializeDecidir = () => {
      try {
        if (window.Decidir && !window.decidir) {
          window.decidir = new window.Decidir(PAYWAY_API_URL, false);

          // setPublishableKey triggers the 404 error, delay ensures interceptors are active
          setTimeout(() => {
            try {
              window.decidir.setPublishableKey(PAYWAY_PUBLIC_KEY);
              window.decidir.setTimeout(0);
            } catch (e) {
            }
          }, 500);

          setIsDecidirLoaded(true);
        } else if (window.decidir) {
          setIsDecidirLoaded(true);
        }
      } catch (error) {
        setIsDecidirLoaded(false);
      }
    };

    loadDecidir().then(_ => {});
  }, []);

  const handleSubmit = (values: any, { setSubmitting }: any) => {
    if (!isDecidirLoaded || !window.decidir) {
      setSubmitting(false);
      return;
    }

    if (!formRef.current) {
      setSubmitting(false);
      return;
    }

    window.decidir.createToken(formRef.current, async (status: number, paywayResponse: any) => {
      if (status === 200 || status === 201) {
        const token = paywayResponse.id;

        props.payment.methodId = PAYMENT_METHOD_ID_PAYWAY;
        props.payment.comments = 'Pago realizado a través de Payway';
        props.payment.cardType = values.cardType;
        props.payment.cardBrand = values.cardBrand;
        const response = await makePostPaywayPaymentCall(props.payment, token);

        if (response.isSuccessful) {
          showSuccessToast({ title: 'Pago exitoso', description: 'Su pago ha sido procesado exitosamente.' });
        } else {
          showErrorToast({ description: 'Se ha producido un error al procesar el pago.' });
        }

        setSubmitting(false);
        navigate(MY_MEMBERSHIP_FEES_PAGE_ROUTE);
      } else {
        console.error('Error token:', paywayResponse);
        setSubmitting(false);
      }
    });
  };

  return (
    <Box
      width="full"
      maxW={{ base: "100%", md: "800px", lg: "1200px", xl: "1400px" }}
      mx="auto"
      py={{ base: 2, md: 4, lg: 6 }}
    >
      <Card
        bg={cardBg}
        borderColor={borderColor}
        borderWidth={{ base: "1px", md: "2px" }}
        borderRadius={{ base: "xl", md: "2xl" }}
        boxShadow={{ base: "lg", md: "2xl" }}
        overflow="hidden"
        _hover={{
          transform: 'translateY(-2px)',
          boxShadow: '3xl'
        }}
        transition="all 0.3s ease-in-out"
        mx="auto"
        width="full"
      >
        <CardHeader bg={headerBg} borderBottom="1px solid" borderColor={borderColor} p={{ base: 3, md: 6 }}>
          <VStack spacing={2} align="start">
            <HStack>
              <Icon as={FaLock} color="green.500" />
              <Heading size={{ base: 'md', md: 'lg' }} color={headingColor}>
                Datos de la tarjeta
              </Heading>
            </HStack>
            <Text color={textColor} fontSize={{ base: 'xs', md: 'sm' }}>
              Ingrese los datos de su tarjeta (débito o crédito) para completar el pago de forma segura
            </Text>
          </VStack>
        </CardHeader>

        <CardBody p={{ base: 3, md: 6, lg: 8 }}>
          <Formik
            initialValues={formData}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, isSubmitting, values }) => (
              <Form ref={formRef}>
                                <Grid
                  templateColumns={{
                    base: '1fr',
                    lg: '1fr 320px',
                    xl: '1fr 350px'
                  }}
                  gap={{ base: 4, md: 6, lg: 8, xl: 10 }}
                  alignItems="start"
                  width="full"
                >
                  {/* Form Fields */}
                  <VStack spacing={{ base: 4, md: 6 }} align="stretch">
                    {/* Card Type and Brand Dropdowns */}
                    <Grid
                      templateColumns={{
                        base: '1fr',
                        md: '1fr 1fr'
                      }}
                      gap={{ base: 4, md: 4 }}
                      width="full"
                    >
                      <GridItem>
                        <FormControl isInvalid={!!errors.cardType && touched.cardType}>
                          <FormLabel color={headingColor} fontWeight="semibold">
                            Tipo de tarjeta
                          </FormLabel>
                          <Field name="cardType">
                            {({ field, form }: FieldProps) => (
                              <Select
                                {...field}
                                placeholder="Seleccione el tipo"
                                borderColor={borderColor}
                                size="lg"
                                onChange={(e) => {
                                  const newCardType = e.target.value;
                                  form.setFieldValue(field.name, newCardType);

                                  // Clear Cabal selection if switching to Prepaid
                                  if (newCardType === 'PREPAID' && values.cardBrand === 'CABAL') {
                                    form.setFieldValue('cardBrand', '');
                                  }
                                }}
                              >
                                <option value="DEBIT">Débito</option>
                                <option value="CREDIT">Crédito</option>
                                <option value="PREPAID">Prepaga</option>
                              </Select>
                            )}
                          </Field>
                          <FormErrorMessage>{errors.cardType}</FormErrorMessage>
                        </FormControl>
                      </GridItem>

                      <GridItem>
                        <FormControl isInvalid={!!errors.cardBrand && touched.cardBrand}>
                          <FormLabel color={headingColor} fontWeight="semibold">
                            Marca
                          </FormLabel>
                          <Field name="cardBrand">
                            {({ field, form }: FieldProps) => (
                              <Select
                                {...field}
                                placeholder="Seleccione la marca"
                                borderColor={borderColor}
                                size="lg"
                                onChange={(e) => {
                                  form.setFieldValue(field.name, e.target.value);
                                }}
                              >
                                <option value="VISA">Visa</option>
                                <option value="MASTERCARD">Mastercard</option>
                                {values.cardType !== 'PREPAID' && (
                                  <option value="CABAL">Cabal</option>
                                )}
                              </Select>
                            )}
                          </Field>
                          <FormErrorMessage>{errors.cardBrand}</FormErrorMessage>
                        </FormControl>
                      </GridItem>
                    </Grid>

                    {/* Card Number */}
                    <FormControl isInvalid={!!errors.cardNumber && touched.cardNumber}>
                      <FormLabel color={headingColor} fontWeight="semibold" display="flex" alignItems="center">
                        <Icon as={FaCreditCard} mr={2} color="green.500" />
                        Número de tarjeta
                      </FormLabel>
                      <Field name="cardNumber">
                        {(props: FieldProps) => <CardNumberInput {...props} />}
                      </Field>
                      <FormErrorMessage>{errors.cardNumber}</FormErrorMessage>
                    </FormControl>

                    {/* Expiration Date and CVV */}
                    <Grid
                      templateColumns={{
                        base: '1fr 1fr',
                        md: '1fr 1fr'
                      }}
                      gap={{ base: 2, md: 4 }}
                      width="full"
                    >
                      <GridItem>
                        <FormControl isInvalid={!!errors.expirationDate && touched.expirationDate}>
                          <FormLabel
                            color={headingColor}
                            fontWeight="semibold"
                            display="flex"
                            alignItems="center"
                            fontSize={{ base: 'sm', md: 'md' }}
                          >
                            <Icon as={FaCalendarAlt} mr={2} color="green.500" />
                            Fecha de expiración
                          </FormLabel>
                          <Field name="expirationDate">
                            {(props: FieldProps) => <ExpirationDateInput {...props} />}
                          </Field>
                          <FormErrorMessage>{errors.expirationDate}</FormErrorMessage>
                        </FormControl>
                      </GridItem>

                      <GridItem>
                        <FormControl isInvalid={!!errors.securityCode && touched.securityCode}>
                          <FormLabel
                            color={headingColor}
                            fontWeight="semibold"
                            fontSize={{ base: 'sm', md: 'md' }}
                          >
                            CVV
                          </FormLabel>
                          <Field name="securityCode">
                            {(props: FieldProps) => (
                              <CvvInput
                                {...props}
                                onFocus={() => setIsCardFlipped(true)}
                                onBlur={() => setIsCardFlipped(false)}
                              />
                            )}
                          </Field>
                          <FormErrorMessage>{errors.securityCode}</FormErrorMessage>
                        </FormControl>
                      </GridItem>
                    </Grid>

                    <Divider my={{ base: 4, md: 6 }} />

                    {/* Cardholder Information */}
                    <VStack spacing={{ base: 4, md: 5 }} align="stretch">
                      <Box>
                        <FormControl isInvalid={!!errors.cardHolder && touched.cardHolder}>
                          <FormLabel
                            color={headingColor}
                            fontWeight="semibold"
                            display="flex"
                            alignItems="center"
                            fontSize={{ base: 'sm', md: 'md' }}
                          >
                            <Icon as={FaUser} mr={2} color="green.500" />
                            Titular de la tarjeta
                          </FormLabel>
                          <Field name="cardHolder">
                            {CardHolderInput}
                          </Field>
                          <FormErrorMessage>{errors.cardHolder}</FormErrorMessage>
                        </FormControl>
                      </Box>

                      <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={{ base: 4, md: 5 }}>
                        <GridItem>
                          <FormControl isInvalid={!!errors.cardHolderId && touched.cardHolderId}>
                            <FormLabel
                              color={headingColor}
                              fontWeight="semibold"
                              display="flex"
                              alignItems="center"
                              fontSize={{ base: 'sm', md: 'md' }}
                            >
                              <Icon as={FaIdCard} mr={2} color="green.500" />
                              Número de documento
                            </FormLabel>
                            <Field name="cardHolderId">
                              {DocumentNumberInput}
                            </Field>
                            <FormErrorMessage>{errors.cardHolderId}</FormErrorMessage>
                          </FormControl>
                        </GridItem>
                        <GridItem display={{ base: 'none', md: 'block' }} />
                      </Grid>
                    </VStack>

                    {/* Hidden fields for Payway */}
                    <input
                      type="hidden"
                      data-decidir="card_expiration_month"
                      value={values.expirationMonth}
                    />
                    <input
                      type="hidden"
                      data-decidir="card_expiration_year"
                      value={values.expirationYear}
                    />
                    <input
                      type="hidden"
                      data-decidir="card_holder_doc_type"
                      value="DNI"
                    />

                  </VStack>

                  {/* Credit Card Preview */}
                  <Box display={{ base: 'none', lg: 'block' }}>
                    <VStack spacing={4} position="sticky" top={4}>
                      <Text color={textColor} fontSize="sm" textAlign="center" fontWeight="medium">
                        Vista previa de su tarjeta
                      </Text>
                      <Flex justify="center">
                        <CreditCardPreview
                          cardNumber={values.cardNumber}
                          cardHolder={values.cardHolder}
                          expirationMonth={values.expirationMonth || ''}
                          expirationYear={values.expirationYear || ''}
                          securityCode={values.securityCode}
                          isFlipped={isCardFlipped}
                          cardType={values.cardType}
                          cardBrand={values.cardBrand}
                        />
                      </Flex>
                      <Text
                         color={textColor}
                         fontSize="xs"
                         textAlign="center"
                         maxW="280px"
                         bg={securityBadgeBg}
                         p={2}
                         borderRadius="md"
                         border="1px solid"
                         borderColor={securityBadgeBorder}
                       >
                        🔒 Sus datos están protegidos y no quedan almacenados.
                      </Text>
                    </VStack>
                  </Box>
                </Grid>

                {/* Centered Submit Button */}
                <Box mt={{ base: 6, md: 8 }} display="flex" justifyContent="center" width="full">
                  <Button
                    type="submit"
                    colorScheme="green"
                    size={{ base: 'lg', md: 'lg' }}
                    isLoading={isSubmitting}
                    loadingText="Procesando pago..."
                    width={{ base: "full", md: "auto" }}
                    minWidth={{ base: "auto", md: "350px" }}
                    height={{ base: '50px', md: '60px' }}
                    fontSize={{ base: 'md', md: 'lg' }}
                    fontWeight="bold"
                    leftIcon={<Icon as={FaLock} />}
                    _hover={{
                      transform: 'translateY(-2px)',
                      boxShadow: 'lg'
                    }}
                    transition="all 0.2s ease-in-out"
                  >
                    Confirmar pago seguro
                  </Button>
                </Box>

                {/* Mobile Card Preview */}
                                <Box
                  display={{ base: 'block', lg: 'none' }}
                  mt={{ base: 4, md: 6 }}
                  pt={{ base: 4, md: 6 }}
                  borderTop="1px solid"
                  borderColor={borderColor}
                >
                  <VStack spacing={4}>
                    <Text color={textColor} fontSize="sm" textAlign="center" fontWeight="medium">
                      Vista previa de su tarjeta
                    </Text>
                    <Flex justify="center">
                      <Box transform={{ base: 'scale(0.8)', md: 'scale(0.9)' }}>
                        <CreditCardPreview
                          cardNumber={values.cardNumber}
                          cardHolder={values.cardHolder}
                          expirationMonth={values.expirationMonth || ''}
                          expirationYear={values.expirationYear || ''}
                          securityCode={values.securityCode}
                          isFlipped={isCardFlipped}
                          cardType={values.cardType}
                          cardBrand={values.cardBrand}
                        />
                      </Box>
                    </Flex>
                                         <Text
                       color={textColor}
                       fontSize="xs"
                       textAlign="center"
                       bg={securityBadgeBg}
                       p={2}
                       borderRadius="md"
                       border="1px solid"
                       borderColor={securityBadgeBorder}
                     >
                      🔒 Datos protegidos con SSL de 256 bits
                    </Text>
                  </VStack>
                </Box>
              </Form>
            )}
          </Formik>
        </CardBody>
      </Card>
    </Box>
  );
};

export default PaywayCheckout;
